import { inject, Injectable, type Provider } from '@angular/core';

import type {
  LanguageConfig,
  PhraseAppTranslations,
  SupportedTranslations,
} from '@core/i18n-legacy';
import {
  I18N_CONFIG_TOKEN,
  I18nService,
  LANGUAGE_CONFIG,
  LOCALE,
  PHRASE_APP_TRANSLATIONS,
  PhraseAppEditorService,
  PhraseAppTranslationService,
  phraseAppTranslationsLoader,
  TranslationsProvider,
} from '@core/i18n-legacy';
import { injectTellentAssistantMobileLocale } from '@core/tellent-assistant-mobile-contracts';
import type { TellentAssistantMobileTranslationSources } from '@core/tellent-assistant-mobile-contracts';

const DEFAULT_LOCALE: SupportedTranslations = 'en';

@Injectable({ providedIn: 'root' })
export class TellentAssistantMobileI18nService {
  private readonly hostLocale = injectTellentAssistantMobileLocale({ optional: true });

  private translations: PhraseAppTranslations[] | null = null;
  private loadedLocale: SupportedTranslations | null = null;

  /**
   * The host owns the set of locales it supports, so we keep no list of our own —
   * we only reduce a regional tag (`de-AT` -> `de`) to something the translation
   * sources can key on.
   *
   * A locale a source does not carry loads as empty rather than failing, and
   * `I18nService.translate` falls back to the default locale per key, so a
   * partially translated locale degrades string by string instead of rendering
   * raw keys. That is what keeps the assistant's own English-only strings
   * readable while the reused ones localise.
   */
  public get locale(): SupportedTranslations {
    const locale = this.hostLocale?.() ?? DEFAULT_LOCALE;
    return <SupportedTranslations>locale.toLowerCase().split('-')[0];
  }

  public async load(sources: TellentAssistantMobileTranslationSources): Promise<void> {
    const locale = this.locale;
    if (this.translations && this.loadedLocale === locale) {
      return;
    }
    this.translations = await phraseAppTranslationsLoader(locale, DEFAULT_LOCALE, sources);
    this.loadedLocale = locale;
  }

  public get loadedTranslations(): PhraseAppTranslations[] {
    return this.translations ?? [];
  }
}

export function buildTellentAssistantMobileI18nProviders(): Provider[] {
  return [
    {
      provide: PHRASE_APP_TRANSLATIONS,
      useFactory: () => inject(TellentAssistantMobileI18nService).loadedTranslations,
    },
    { provide: TranslationsProvider, useExisting: PhraseAppTranslationService },
    {
      provide: LOCALE,
      useFactory: () => inject(TellentAssistantMobileI18nService).locale,
    },
    { provide: I18N_CONFIG_TOKEN, useValue: { editorEnabled: false, phraseAppProjectId: '' } },
    {
      provide: LANGUAGE_CONFIG,
      useFactory: (): LanguageConfig => ({
        defaultLanguage: DEFAULT_LOCALE,
        // setLocale refuses anything absent here, so the current locale must be
        // listed alongside the fallback it degrades to.
        languages: [...new Set([DEFAULT_LOCALE, inject(TellentAssistantMobileI18nService).locale])],
      }),
    },
    I18nService,
    PhraseAppTranslationService,
    PhraseAppEditorService,
  ];
}
